import type { ChartAccount } from '@/api/models';
import {
    buildAccountTree,
    type AccountTreeNode,
} from '@/pages/accounting/books/chartOfAccounts/utils/buildAccountTree';

/**
 * The slot account-filter picker's pure state helpers.
 *
 * Keyed by account **code**, not id — `accountFilter` is already `string[]` of
 * codes on the wire (matched with `code.startsWith(prefix)`, see
 * `pairBoxMeta.ts`), and every code is unique and stable, so the picker can
 * read and write it directly with no id⇌ode translation.
 */

export type AccountFilterOption = {
    code: string;
    name: string;
    /** `"code name"` — what the picker's search matches against. */
    searchText: string;
};

export type AccountFilterSection = {
    /** The section header's own account id — a stable React key. */
    id: string;
    label: string;
    options: AccountFilterOption[];
};

const toOption = (account: ChartAccount): AccountFilterOption => ({
    code: account.code,
    name: account.name,
    searchText: `${account.code} ${account.name}`,
});

const flatten = (node: AccountTreeNode, out: AccountFilterOption[]): void => {
    out.push(toOption(node.account));
    node.children.forEach((child) => flatten(child, out));
};

/**
 * Every live account — group header or postable leaf — as selectable filter
 * options, sectioned by top-level class. A group's own code is exactly what
 * `accountFilter`'s prefix matching already expects, so picking
 * "11 — Non-current tangible assets" behaves exactly like typing "11" did.
 *
 * Reuses the same `buildAccountTree` the ledger screen builds its tree from,
 * archived accounts dropped by its default `showArchived: false`.
 */
export const accountFilterSections = (
    accounts: readonly ChartAccount[],
): AccountFilterSection[] => {
    const { roots } = buildAccountTree(accounts as ChartAccount[]);
    return roots.map((root) => {
        const options: AccountFilterOption[] = [];
        flatten(root, options);
        return {
            id: root.account.id,
            label: `${root.account.code} — ${root.account.name}`,
            options,
        };
    });
};

/** Every code the picker currently offers, across every section. */
export const knownFilterCodes = (
    accounts: readonly ChartAccount[],
): Set<string> => {
    const codes = new Set<string>();
    accountFilterSections(accounts).forEach((section) =>
        section.options.forEach((option) => codes.add(option.code)),
    );
    return codes;
};

/**
 * Codes in `filter` the picker doesn't recognize — archived, renamed, or
 * from a chart that no longer exists. Kept, not dropped: an old template's
 * filter is data the editor must never silently discard because the picker
 * can't currently offer it.
 */
export const staleFilterCodes = (
    filter: readonly string[],
    accounts: readonly ChartAccount[],
): string[] => {
    const known = knownFilterCodes(accounts);
    return filter.filter((code) => !known.has(code));
};

/**
 * The one account or group a filter resolves to — only when it is exactly
 * one code and the picker recognizes it. `null` for an empty, ambiguous
 * (more than one code), or unmatched filter; each is a case the label
 * auto-suggestion deliberately leaves alone.
 */
export const soleResolvedAccount = (
    filter: readonly string[],
    accounts: readonly ChartAccount[],
): ChartAccount | null => {
    const [code, ...rest] = filter;
    if (!code || rest.length > 0 || !knownFilterCodes(accounts).has(code)) {
        return null;
    }
    return accounts.find((account) => account.code === code) ?? null;
};
